import {
  Box,
  Container,
  Fade,
  IconButton,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  AlertComponent,
  DialogComponent,
  NavBar,
  ShowContent,
} from "../../components";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DesignerContext from "../../context/DesignerContext";
import {
  modernDataGridStyles,
  modernIconButtonStyles,
} from "../../utils/dataGridStyles";

export default function PullingDesignList() {
  const {
    fetchPullingList,
    fetchDataPulling,
    handleDeletePulling,
    pullingList,
    isSuccess,
    isError,
  } = useContext(DesignerContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const matches = useMediaQuery("(min-width:600px)");

  useEffect(() => {
    fetchPullingList();
  }, [fetchPullingList]);
  const columns: GridColDef[] = [
    {
      field: "client",
      headerName: "Client",
      renderHeader: () => <strong>{"CLIENT"}</strong>,
      sortable: true,
      width: 180,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.well.client.name || "",
      renderCell: (params: any) => {
        return (
          <Link
            to={`/pulling/${params.row?.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.row?.well.client.name}
          </Link>
        );
      },
    },
    {
      field: "well",
      headerName: "Well",
      renderHeader: () => <strong>{"WELL"}</strong>,
      sortable: true,
      width: 180,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.well.name || "",
    },
    {
      field: "customName",
      headerName: "Description",
      renderHeader: () => <strong>{"DESCRIPTION"}</strong>,
      width: 180,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.pullingName + " - " + params.row?.customName,
    },
    {
      field: "action",
      headerName: "Action",
      renderHeader: () => <strong>{"ACTION"}</strong>,
      width: 240,
      filterable: false,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Box className="action-buttons" sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton
                onClick={() => navigate(`/pulling/${params.row?.id}`)}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate">
              <IconButton
                onClick={() => {
                  fetchDataPulling(params.row?.id);
                  navigate("/pulling/0");
                }}
                size="small"
                sx={modernIconButtonStyles(theme)}
              >
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton
                onClick={() => navigate(`/pulling/${params.row?.id}?pdf=true`)}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => {
                  setIdToDelete(params.row?.id);
                  setIsOpenAlert(true);
                }}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const buttons = [
    {
      title: "New",
      action: () => {
        navigate("/pulling/0");
      },
      icon: <AddBoxIcon />,
    },
  ];
  let content: JSX.Element | JSX.Element[];

  content = (
    <>
      <NavBar title="Pulling Reports" buttons={buttons} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <Box sx={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  sx={modernDataGridStyles(theme)}
                  density={matches ? "standard" : "compact"}
                  rows={pullingList}
                  columns={columns}
                  slots={{ toolbar: GridToolbar }}
                  loading={false}
                  paginationMode="server"
                />
              </Box>
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this item?</div>}
          onAccept={() => {
            handleDeletePulling(idToDelete);
            setIdToDelete("");
            setIsOpenAlert(false);
          }}
          onCancel={() => {
            setIsOpenAlert(false);
            setIdToDelete("");
          }}
        />
      )}
    </>
  );

  return <ShowContent error={""} isLoading={false} content={content} />;
}
